import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Chip, MenuItem, Select, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  datePickerStyle,
  numberInputStyle,
  selectFieldStyle,
  selectMenuPaperStyle,
  textFieldInputStyle
} from './styles';
import { AritoFormV2Context } from '@/components/custom/erp/form-v2/context';
import { AritoInputTable } from '@/components/custom/arito/input-table';
import { useFormFieldHandlers } from './hooks/useFormFieldHandlers';
import { Checkbox } from '@/components/ui/checkbox';
import type { AritoFormFieldProps } from '../types';
import { AritoFormContext } from '../form/context';
import { formatMoney } from '@/lib/formatUtils';
import { Label } from '@/components/ui/label';
import { SearchField } from './components';
import { cn } from '@/lib/utils';

const EMPTY_OPTIONS: { value: any; label: string }[] = [];
const EMPTY_COLUMNS: any[] = [];

const parseNumber = (formattedValue: string): number | null => {
  if (!formattedValue) return null;

  const cleanValue = formattedValue.replace(/[^\d.-]/g, '');
  return parseFloat(cleanValue) || null;
};

type SearchFieldControllerProps = Omit<
  AritoFormFieldProps<any>,
  'children' | 'defaultValue' | 'control' | 'withSearch' | 'withDate'
> & {
  field: any;
  fieldId: string;
};

const SearchFieldController = memo(
  ({
    field,
    fieldId,
    label,
    disabled = false,
    type = 'text',
    searchEndpoint,
    searchColumns,
    searchResultLabelKey,
    searchResultValueKey,
    defaultSearchColumn,
    displayRelatedField,
    inputClassName,
    dialogTitle,
    hideRowNumberColumn,
    actionButtons,
    headerFields,
    tabs,
    placeholder
  }: SearchFieldControllerProps) => {
    const {
      searchDialogOpen,
      setSearchDialogOpen,
      searchQuery,
      searchResults,
      selectedSearchResult,
      setSelectedSearchResult,
      displayText,
      setDisplayText,
      relatedFieldValue,
      setRelatedFieldValue,
      isFullScreen,
      handleSearchClick,
      handleSearchQueryChange,
      handleSelectSearchResult,
      handleConfirmSelection,
      toggleFullScreen
    } = useFormFieldHandlers({
      searchEndpoint,
      searchResultLabelKey,
      defaultSearchColumn,
      displayRelatedField
    });

    return (
      <SearchField
        field={field}
        label={label}
        disabled={disabled}
        fieldId={fieldId}
        type={type}
        searchEndpoint={searchEndpoint}
        searchColumns={searchColumns}
        searchResultLabelKey={searchResultLabelKey}
        searchResultValueKey={searchResultValueKey}
        className={inputClassName}
        defaultSearchColumn={defaultSearchColumn}
        dialogTitle={dialogTitle}
        hideRowNumberColumn={hideRowNumberColumn}
        actionButtons={actionButtons}
        headerFields={headerFields}
        tabs={tabs}
        displayRelatedField={!!displayRelatedField}
        relatedFieldValue={relatedFieldValue}
        setRelatedFieldValue={setRelatedFieldValue}
        searchDialogOpen={searchDialogOpen}
        setSearchDialogOpen={setSearchDialogOpen}
        searchQuery={searchQuery}
        searchResults={searchResults}
        selectedSearchResult={selectedSearchResult}
        setSelectedSearchResult={setSelectedSearchResult}
        displayText={displayText}
        setDisplayText={setDisplayText}
        isFullScreen={isFullScreen}
        handleSearchClick={handleSearchClick}
        handleSearchQueryChange={handleSearchQueryChange}
        handleSelectSearchResult={handleSelectSearchResult}
        handleConfirmSelection={handleConfirmSelection}
        toggleFullScreen={toggleFullScreen}
        placeholder={placeholder}
      />
    );
  }
);

SearchFieldController.displayName = 'SearchFieldController';

export const FormField = memo(
  ({
    label,
    value,
    onValueChange,
    onBlur,
    name = '',
    type = 'text',
    disabled = false,
    options,
    error,
    className,
    columns,
    searchEndpoint,
    searchResultLabelKey,
    searchResultValueKey,
    searchColumns,
    defaultSearchColumn,
    displayRelatedField,
    placeholder,
    labelClassName = 'shrink-0',
    inputClassName,
    dialogTitle,
    hideRowNumberColumn,
    actionButtons,
    headerFields,
    tableActionButtons,
    tabs,
    transform
  }: AritoFormFieldProps<any>) => {
    const v2Context = useContext(AritoFormV2Context);
    const legacyContext = useContext(AritoFormContext);
    const { control, errors } = v2Context || legacyContext || {};
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const fieldError = error || errors?.[name]?.message;
    const fieldId = name;
    const isSearchField = Boolean(searchEndpoint);
    const resolvedOptions = options || EMPTY_OPTIONS;
    const resolvedColumns = columns || EMPTY_COLUMNS;

    useEffect(() => {
      if ((type !== 'number' && type !== 'number-formatted') || isFocused) {
        return;
      }

      const nextValue = value !== undefined && value !== null ? String(value) : '';
      setDisplayValue(type === 'number-formatted' && nextValue ? formatMoney(nextValue as any) : nextValue);
    }, [isFocused, type, value]);

    const labelNode = useMemo(() => {
      if (type === 'checkbox' || type === 'table' || !label) {
        return null;
      }

      return (
        <Label
          htmlFor={fieldId}
          className={cn('mt-3 flex items-center pr-2 text-left text-sm font-normal sm:mb-0', labelClassName)}
        >
          {label}
        </Label>
      );
    }, [fieldId, label, labelClassName, type]);

    return (
      <div>
        <div className={cn('flex items-center gap-1', className)}>
          {labelNode}

          <div className={cn('w-full', className)}>
            <Controller
              name={name}
              control={control}
              render={({ field }) => {
                if (isSearchField) {
                  return (
                    <SearchFieldController
                      field={field}
                      fieldId={fieldId}
                      label={label}
                      disabled={disabled}
                      type={type}
                      searchEndpoint={searchEndpoint}
                      searchColumns={searchColumns}
                      searchResultLabelKey={searchResultLabelKey}
                      searchResultValueKey={searchResultValueKey}
                      inputClassName={inputClassName}
                      defaultSearchColumn={defaultSearchColumn}
                      dialogTitle={dialogTitle}
                      hideRowNumberColumn={hideRowNumberColumn}
                      actionButtons={actionButtons}
                      headerFields={headerFields}
                      tabs={tabs}
                      displayRelatedField={displayRelatedField}
                      placeholder={placeholder}
                    />
                  );
                }

                switch (type) {
                  case 'table':
                    return (
                      <AritoInputTable
                        value={field.value ? field.value : []}
                        columns={resolvedColumns}
                        onChange={field.onChange}
                        mode={disabled ? 'view' : 'edit'}
                        tableActionButtons={tableActionButtons}
                      />
                    );
                  case 'number-formatted': {
                    const currentValue = value !== undefined ? value : field.value !== undefined ? field.value : '';

                    return (
                      <TextField
                        type='text'
                        disabled={disabled}
                        fullWidth
                        className={className}
                        size='small'
                        variant='standard'
                        id={fieldId}
                        value={isFocused ? displayValue : formatMoney(currentValue as any)}
                        onFocus={() => {
                          setIsFocused(true);
                          setDisplayValue(
                            currentValue !== undefined && currentValue !== null ? String(currentValue) : ''
                          );
                        }}
                        onBlur={e => {
                          field.onBlur?.();
                          onBlur?.(e as any);
                          setIsFocused(false);
                          const numericValue = parseNumber(e.target.value);

                          if (onValueChange) {
                            onValueChange(numericValue as any);
                            return;
                          }

                          field.onChange(numericValue as any);
                        }}
                        onChange={e => {
                          const inputValue = e.target.value;
                          setDisplayValue(inputValue);
                          const parsedValue = parseNumber(inputValue.replace(/[^\d.,-]/g, ''));

                          if (onValueChange) {
                            onValueChange(parsedValue as any);
                            return;
                          }

                          field.onChange(parsedValue as any);
                        }}
                        placeholder={disabled ? undefined : placeholder}
                        sx={{
                          ...numberInputStyle(type),
                          '& .MuiInputBase-input::placeholder': { opacity: 1, color: 'rgba(0, 0, 0, 1)' },
                          '& .MuiInput-input': { textAlign: 'right' }
                        }}
                      />
                    );
                  }
                  case 'select':
                    return (
                      <Select
                        {...field}
                        disabled={disabled}
                        size='small'
                        fullWidth
                        displayEmpty
                        id={fieldId}
                        sx={selectFieldStyle}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              ...selectMenuPaperStyle,
                              maxWidth: '240px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              '& .MuiMenuItem-root': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                padding: '8px 16px',
                                minHeight: 'auto',
                                lineHeight: 1.4
                              }
                            }
                          }
                        }}
                      >
                        {resolvedOptions.map(option => (
                          <MenuItem
                            key={option.value.toString()}
                            value={option.value}
                            className='whitespace-normal break-words'
                            onClick={() => {
                              onValueChange?.(option.value);
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    );
                  case 'checkbox':
                    return (
                      <div className='flex items-center space-x-2'>
                        <Checkbox
                          id={fieldId}
                          checked={field.value === undefined ? !!value : !!field.value}
                          onCheckedChange={checked => {
                            field.onChange?.(checked);
                            onValueChange?.(checked);
                          }}
                          disabled={disabled}
                          className='size-4 rounded-sm border-gray-300'
                        />
                        <Label htmlFor={fieldId} className={cn('text-sm text-black', labelClassName)}>
                          {label}
                        </Label>
                      </div>
                    );
                  case 'date':
                    return (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(newValue: dayjs.Dayjs | null) => {
                            const formattedValue = newValue ? newValue.format('YYYY-MM-DD') : null;
                            field.onChange(formattedValue);
                            onValueChange?.(formattedValue as any);
                          }}
                          disabled={disabled}
                          format='DD/MM/YYYY'
                          localeText={{
                            cancelButtonLabel: 'Hủy',
                            toolbarTitle: 'Chọn ngày',
                            todayButtonLabel: 'Hôm nay',
                            previousMonth: 'Tháng trước',
                            nextMonth: 'Tháng sau'
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              variant: 'standard',
                              inputProps: { id: fieldId },
                              sx: datePickerStyle
                            }
                          }}
                        />
                      </LocalizationProvider>
                    );
                  case 'multiselect':
                    return (
                      <Select
                        {...field}
                        disabled={disabled}
                        size='small'
                        fullWidth
                        multiple
                        id={fieldId}
                        value={field.value || []}
                        sx={selectFieldStyle}
                        renderValue={selected => (
                          <div className='flex flex-wrap gap-1'>
                            {(selected as string[]).map(selectedValue => {
                              const selectedLabel = resolvedOptions.find(
                                option => option.value === selectedValue
                              )?.label;
                              return (
                                <Chip
                                  key={selectedValue}
                                  label={selectedLabel}
                                  size='small'
                                  className='m-0.5 h-5 bg-gray-100 text-xs'
                                />
                              );
                            })}
                          </div>
                        )}
                        MenuProps={{
                          PaperProps: {
                            sx: selectMenuPaperStyle
                          }
                        }}
                      >
                        {resolvedOptions.map(option => (
                          <MenuItem key={option.value.toString()} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    );
                  default: {
                    const currentValue = value !== undefined ? value : field.value !== undefined ? field.value : '';
                    const isNumberType = type === 'number';
                    const resolvedValue =
                      currentValue !== undefined && currentValue !== null ? String(currentValue) : '';

                    return (
                      <TextField
                        {...field}
                        type={isNumberType ? 'text' : type}
                        disabled={disabled}
                        fullWidth
                        size='small'
                        variant='standard'
                        id={fieldId}
                        value={isNumberType ? (isFocused ? displayValue : resolvedValue) : resolvedValue}
                        onFocus={() => {
                          if (!isNumberType) {
                            return;
                          }

                          setIsFocused(true);
                          setDisplayValue(currentValue === 0 || currentValue === '0' ? '' : String(currentValue || ''));
                        }}
                        onBlur={e => {
                          field.onBlur?.();
                          onBlur?.(e as any);

                          if (!isNumberType) {
                            return;
                          }

                          setIsFocused(false);
                          const numericValue = parseNumber(e.target.value);
                          const transformedValue = transform
                            ? transform(numericValue !== null ? String(numericValue) : '')
                            : numericValue;

                          if (onValueChange) {
                            onValueChange(transformedValue);
                            return;
                          }

                          field.onChange(transformedValue);
                        }}
                        onChange={e => {
                          const inputValue = e.target.value;

                          if (isNumberType) {
                            let processedValue = inputValue;

                            if ((displayValue === '0' || displayValue === '') && inputValue.length > 0) {
                              if (inputValue.match(/^0+[1-9]/)) {
                                processedValue = inputValue.replace(/^0+/, '');
                              } else if (inputValue === '0' && displayValue === '') {
                                processedValue = '0';
                              } else if (displayValue === '0' && inputValue.length === 1 && /[1-9]/.test(inputValue)) {
                                processedValue = inputValue;
                              }
                            }

                            setDisplayValue(processedValue);
                            const parsedValue = parseNumber(processedValue.replace(/[^\d.,-]/g, ''));

                            if (onValueChange) {
                              onValueChange(parsedValue);
                              return;
                            }

                            field.onChange(parsedValue);
                            return;
                          }

                          const transformedValue = transform ? transform(inputValue) : inputValue;

                          if (onValueChange) {
                            onValueChange(transformedValue);
                            return;
                          }

                          field.onChange(transformedValue);
                        }}
                        placeholder={disabled ? undefined : placeholder}
                        sx={{
                          ...numberInputStyle(type),
                          '& .MuiInputBase-input::placeholder': {
                            opacity: 1,
                            color: 'rgba(0, 0, 0, 0.6)'
                          },
                          ...(isNumberType && {
                            '& .MuiInput-input': {
                              textAlign: 'right'
                            }
                          })
                        }}
                      />
                    );
                  }
                }
              }}
            />
          </div>
        </div>

        {fieldError && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
